<template>
  <div class="paid-page">
    <div class="paid-page__container">
      <nav class="paid-page__breadcrumbs" aria-label="Хлебные крошки">
        <ol>
          <li>
            <router-link to="/">Главная</router-link>
            <img src="@/assets/svg/arrow-mini.svg" alt="" />
          </li>
          <li>
            <span aria-current="page">Платные услуги и цены</span>
          </li>
        </ol>
      </nav>

      <AppLoader v-if="isLoading"/>

      <template v-else>
        <section class="paid-hero">
          <div class="paid-hero__inner">
            <div class="paid-hero__content">
              <div class="paid-hero__text">
                <h1 class="paid-hero__title">Платные услуги и цены</h1>
                <p class="paid-hero__lead">
                  Актуальный прайс на медицинские услуги. Стоимость может меняться.
                  Уточняйте информацию у администраторов по телефону
                  <a href="tel:+73852591130">+7 (3852) 591-130</a>.
                </p>
              </div>
              <div class="paid-hero__search paid-search">
                <img src="@/assets/svg/search.svg" alt="" class="paid-search__icon" />
                <input
                    v-model="searchQuery"
                    type="search"
                    class="paid-search__input"
                    placeholder="Введите для поиска..."
                    aria-label="Поиск по сайту"
                    @input="onSearchInput"
                />
                <div
                    v-if="searchQuery.trim() && searchResults.length"
                    class="paid-hero__results search-results search-results--scrollable"
                >
                  <ul class="paid-hero__results-list">
                    <li v-for="result in searchResults" :key="result.key" class="result-item">
                      <router-link :to="result.path" class="result-link" @click.native="clearSearch">
                        {{ result.meta.title }}
                        <p v-if="result.meta.description" class="result-description">{{ result.meta.description }}</p>
                      </router-link>
                    </li>
                  </ul>
                </div>
                <p
                    v-else-if="searchQuery.trim() && !searchResults.length"
                    class="paid-hero__empty no-results"
                >
                  Ничего не найдено по запросу «{{ searchQuery.trim() }}»
                </p>
              </div>
            </div>
            <div class="paid-hero__image" aria-hidden="true">
              <img
                class="paid-hero__image-img"
                src="@/assets/img/paid-services/paid-hero.png"
                alt=""
              />
            </div>
          </div>
        </section>

        <section v-if="popularGroups.length" class="paid-popular" aria-labelledby="paid-popular-title">
          <h2 id="paid-popular-title" class="paid-popular__title">Популярные услуги</h2>
          <div class="paid-popular__grid">
            <button
                v-for="group in popularGroups"
                :key="'pop-' + group.groupName"
                type="button"
                class="paid-popular__card"
                @click="openPopularGroup(group.groupName)"
            >
              <span class="paid-popular__icon" aria-hidden="true">
                <img class="paid-popular__icon-img" :src="groupIconSrc(group.groupName)" alt="" />
              </span>
              <span class="paid-popular__name">{{ group.groupName }}</span>
            </button>
          </div>
        </section>

        <section class="paid-all" aria-labelledby="paid-all-title">
          <div class="paid-all__toolbar">
            <h2 id="paid-all-title" class="paid-all__title">Все услуги</h2>
            <button
                type="button"
                class="paid-all__toggle"
                @click="toggleAllAccordions"
            >
              {{ allExpanded ? 'Свернуть все' : 'Развернуть все' }}
              <span class="paid-all__toggle-icon" :class="{ 'is-open': allExpanded }" aria-hidden="true">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18">
                  <path fill="currentColor" d="M7.41 15.41 12 10.83l4.59 4.58L18 14l-6-6-6 6z"/>
                </svg>
              </span>
            </button>
          </div>

          <div class="paid-accordion">
            <div
                v-for="itemDepartment in paidList"
                :key="itemDepartment.groupName"
                class="paid-accordion__item"
                :class="{ 'paid-accordion__item--highlight': highlightedGroupAnchor === groupAnchorId(itemDepartment.groupName) }"
                :id="groupAnchorId(itemDepartment.groupName)"
            >
              <button
                  type="button"
                  class="paid-accordion__btn"
                  :aria-expanded="isAccordionOpen(itemDepartment.groupName) ? 'true' : 'false'"
                  @click="toggleAccordion(itemDepartment.groupName)"
              >
                <span class="paid-accordion__btn-left">
                  <span class="paid-accordion__icon" aria-hidden="true">
                    <img class="paid-accordion__icon-img" :src="groupIconSrc(itemDepartment.groupName)" alt="" />
                  </span>
                  <span class="paid-accordion__name">{{ itemDepartment.groupName }}</span>
                </span>
                <span
                    class="paid-accordion__chevron"
                    :class="{ 'is-open': isAccordionOpen(itemDepartment.groupName) }"
                    aria-hidden="true"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20">
                    <path fill="currentColor" d="M7.41 8.59 12 13.17l4.59-4.58L18 10l-6 6-6-6z"/>
                  </svg>
                </span>
              </button>

              <div v-if="isAccordionOpen(itemDepartment.groupName)" class="paid-accordion__panel">
                <ul class="paid-service-list">
                  <template v-for="(service, index) in itemDepartment.services">
                    <AppPaidServicePackageRow
                      v-if="hasPaidPackage(service)"
                      :key="service.id != null ? `pkg-${service.id}` : `pkg-${index}`"
                      :service="service"
                      :paid-groups="paid"
                      :open="isPackageOpen(service.id)"
                      :highlighted="isHighlightedService(service.id)"
                      :row-id="paidServiceRowId(service.id)"
                      :format-price="formatPrice"
                      @toggle="togglePackage(service.id)"
                    />
                    <li
                      v-else
                      :key="service.id != null ? `svc-${service.id}` : index"
                      class="paid-service-row"
                      :id="paidServiceRowId(service.id)"
                      :class="{ 'paid-service-row--highlight': isHighlightedService(service.id) }"
                    >
                      <span class="paid-service-row__name">{{ service.name }}</span>
                      <span class="paid-service-row__price">{{ formatPrice(service.price) }}</span>
                    </li>
                  </template>
                </ul>
              </div>
            </div>
          </div>

          <div class="paid-docs">
            <button
                type="button"
                class="paid-accordion__btn paid-docs__btn"
                :aria-expanded="isOpenInfo ? 'true' : 'false'"
                @click="toggleAccordionInfo"
            >
              <span class="paid-accordion__btn-left">
                <span class="paid-accordion__icon" aria-hidden="true" v-html="icons.doc"></span>
                <span class="paid-accordion__name">Информация о платных услугах</span>
              </span>
              <span
                  class="paid-accordion__chevron"
                  :class="{ 'is-open': isOpenInfo }"
                  aria-hidden="true"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20">
                  <path fill="currentColor" d="M7.41 8.59 12 13.17l4.59-4.58L18 10l-6 6-6-6z"/>
                </svg>
              </span>
            </button>
            <div v-if="isOpenInfo" class="paid-accordion__panel paid-docs__panel">
              <p class="paid-docs__text">
                Всю дополнительную информацию можете найти в разделе документы по ссылке ниже:
              </p>
              <button type="button" class="paid-docs__link" @click="routePaidServices">
                Документы — платные услуги
              </button>
            </div>
          </div>
        </section>

        <section class="paid-bottom" aria-label="Скачать прайс и нормативная информация">
          <button
            type="button"
            class="paid-bottom__download"
            :disabled="downloadingPriceXlsx"
            @click="downloadPriceXlsx"
          >
            {{ downloadingPriceXlsx ? 'Загрузка…' : 'Скачать прайс-лист (Excel)' }}
          </button>

          <div class="paid-normative" aria-labelledby="paid-normative-title">
            <h2 id="paid-normative-title" class="paid-normative__title">Нормативная информация</h2>
            <ul class="paid-normative__list">
              <li>
                <a href="http://pravo.gov.ru" target="_blank" rel="noopener noreferrer">Официальный интернет-портал правовой информации (pravo.gov.ru)</a>
              </li>
              <li>
                <a href="https://cr.minzdrav.gov.ru/" target="_blank" rel="noopener noreferrer">Рубрикатор клинических рекомендаций Минздрава России</a>
              </li>
              <li>
                <router-link to="/documents/paid-services">Документы по платным услугам</router-link>
              </li>
              <li>
                С 01.09.2026 действует
                <a href="http://publication.pravo.gov.ru" target="_blank" rel="noopener noreferrer">Постановление Правительства РФ № 659</a>
                об утверждении Правил предоставления медицинскими организациями платных медицинских услуг.
              </li>
            </ul>
          </div>
        </section>
      </template>
    </div>
  </div>
</template>

<script>
  import { actionTypesPaid } from '@/store/modules/paidServices'
  import { actionTypesNews } from '@/store/modules/news'
  import { actionTypesDepartment } from '@/store/modules/department'
  import { actionTypes as actionTypesPages } from '@/store/modules/pages'
  import { actionTypesDocuments } from '@/store/modules/documents'
  import { mapState } from 'vuex'
  import AppLoader from '@/components/Loader.vue'
  import AppPaidServicePackageRow from '@/components/Blocks/AppPaidServicePackageRow.vue'
  import { performSiteSearch } from '@/utils/siteSearch'
  import { getPaidPackage } from '@/utils/paidPackages'

  const POPULAR_PRIORITY = [
    'Ведение беременности',
    'Женское здоровье и гинекология',
    'Анестезия и интенсивная терапия',
    'Генетика и пренатальная диагностика',
    'Немедицинские услуги',
  ]

  const POPULAR_LIMIT = 5

  const PRICE_XLSX_FILE_NAME = 'Прайс-лист ДАР (актуальный).xlsx'
  const PRICE_XLSX_FOLDER_PATH = '/app/documents/Платные услуги/Прейскуранты цен на платные услуги'

  const DOC_ICON = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none"><path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-6z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/><path d="M14 2v6h6M9 13h6M9 17h6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>'

  const GROUP_ICONS = {
    pregnancy: require('@/assets/img/paid-services/icon-pregnancy.png'),
    hospital: require('@/assets/img/paid-services/icon-hospital.png'),
    family: require('@/assets/ico/family.png'),
    repro: require('@/assets/ico/repro.png'),
    gynecology: require('@/assets/img/paid-services/icon-gynecology.png'),
    consult: require('@/assets/ico/consult.png'),
    scan: require('@/assets/ico/scan.png'),
    lab: require('@/assets/ico/lab.png'),
    pathology: require('@/assets/ico/pathology.png'),
    anesthesia: require('@/assets/img/paid-services/icon-anesthesia.png'),
    support: require('@/assets/ico/support.png'),
    comfort: require('@/assets/img/paid-services/icon-nonmedical.png'),
    education: require('@/assets/ico/education.png'),
    postpartum: require('@/assets/ico/postpartum.png'),
    genetics: require('@/assets/img/paid-services/icon-genetics.png'),
    childbirth: require('@/assets/ico/childbirth.png'),
    default: require('@/assets/ico/consult.png'),
  }

  export default {
    name: "AppPaidServices",
    components: { AppLoader, AppPaidServicePackageRow },
    metaInfo() {
      return {
        title: 'Платные услуги и цены | Алтайский краевой клинический перинатальный центр ДАР',
        meta: [
          {
            name: 'description',
            content: 'Актуальный прайс на платные медицинские услуги Алтайского краевого клинического перинатального центра ДАР. Стоимость консультаций, диагностики, лабораторных исследований и других услуг.'
          },
          {
            name: 'keywords',
            content: 'платные услуги, цены, прайс, медицинские услуги, диагностика, лечение, перинатальный центр ДАР'
          }
        ]
      };
    },

    data() {
      return {
        openedDepartments: {},
        openedPackages: {},
        isOpenInfo: false,
        searchQuery: '',
        searchResults: [],
        highlightedServiceId: null,
        highlightedGroupAnchor: '',
        highlightClearTimer: null,
        icons: { doc: DOC_ICON },
        downloadingPriceXlsx: false,
      };
    },
    computed: {
      ...mapState({
        paid: (state) => state.paidServices.paid,
        isLoading: (state) => state.paidServices.isLoading,
        news: (state) => state.news.news,
        employees: (state) => state.department.EMPLOYEES,
        managements: (state) => state.department.MANAGEMENTS,
        chiefMedicalOfficer: (state) => state.department.CHIEF_MEDICAL_OFFICER,
        publishedPages: (state) => state.pages.menuItems,
      }),
      paidList() {
        return this.paid || [];
      },
      popularGroups() {
        const list = this.paidList;
        if (!list.length) {
          return [];
        }
        const byName = {};
        list.forEach((g) => {
          if (g && g.groupName) {
            byName[g.groupName] = g;
          }
        });
        const picked = [];
        POPULAR_PRIORITY.forEach((name) => {
          if (byName[name] && picked.length < POPULAR_LIMIT) {
            picked.push(byName[name]);
          }
        });
        if (picked.length < POPULAR_LIMIT) {
          list.forEach((g) => {
            if (picked.length >= POPULAR_LIMIT) {
              return;
            }
            if (!picked.some((p) => p.groupName === g.groupName)) {
              picked.push(g);
            }
          });
        }
        return picked;
      },
      allExpanded() {
        const groups = this.paidList;
        if (!groups.length) {
          return false;
        }
        return groups.every((g) => this.isAccordionOpen(g.groupName));
      },
    },
    watch: {
      "$route.query": {
        deep: true,
        handler() {
          this.$nextTick(() => this.applyServiceFromRoute());
        },
      },
      paid() {
        if (this.normalizeQueryParam(this.$route.query.serviceId) && this.paid && this.paid.length) {
          this.$nextTick(() => this.applyServiceFromRoute());
        }
        this.runSearch(this.searchQuery);
      },
      searchQuery(val) {
        this.runSearch(val);
      },
      publishedPages: { handler() { this.runSearch(this.searchQuery); }, deep: true },
      news: { handler() { this.runSearch(this.searchQuery); }, deep: true },
      employees: { handler() { this.runSearch(this.searchQuery); }, deep: true },
      managements: { handler() { this.runSearch(this.searchQuery); }, deep: true },
      chiefMedicalOfficer: { handler() { this.runSearch(this.searchQuery); }, deep: true },
    },
    methods: {
      groupIconKey(groupName) {
        const n = String(groupName || '').toLowerCase();
        if (n.includes('беременност')) return 'pregnancy';
        if (n.includes('послеродов') || n.includes('новорожд')) return 'postpartum';
        if (n.includes('стационар') || n.includes('дневн')) return 'hospital';
        if (n.includes('гинеколог')) return 'gynecology';
        if (n.includes('эко') || (n.includes('репродуктив') && !n.includes('семьи'))) return 'repro';
        if (n.includes('семьи') || n.includes('репродук')) return 'family';
        if (n.includes('консультат')) return 'consult';
        if (n.includes('лучев') || n.includes('функциональн')) return 'scan';
        if (n.includes('патологоанатом') || n.includes('иммуногист')) return 'pathology';
        if (n.includes('лаборатор')) return 'lab';
        if (n.includes('анестез') || n.includes('реанимац')) return 'anesthesia';
        if (n.includes('поддержк') || n.includes('социальн')) return 'support';
        if (n.includes('немедицин')) return 'comfort';
        if (n.includes('обучен')) return 'education';
        if (n.includes('генетик') || n.includes('пренатал')) return 'genetics';
        if (n.includes('род') && !n.includes('беременност') && !n.includes('послеродов')) return 'childbirth';
        return 'default';
      },
      groupIconSrc(groupName) {
        return GROUP_ICONS[this.groupIconKey(groupName)] || GROUP_ICONS.default;
      },
      groupAnchorId(groupName) {
        return `paid-group-${encodeURIComponent(String(groupName || ''))}`;
      },
      formatPrice(price) {
        const raw = String(price == null ? '' : price).replace(/\s/g, '').replace(',', '.');
        const num = Number(raw);
        if (!Number.isFinite(num)) {
          const fallback = String(price == null ? '' : price).trim();
          return fallback ? `${fallback} ₽` : '—';
        }
        const formatted = Math.round(num)
            .toString()
            .replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
        return `${formatted} ₽`;
      },
      async downloadPriceXlsx() {
        if (this.downloadingPriceXlsx) return;
        this.downloadingPriceXlsx = true;
        try {
          const reportData = await this.$store.dispatch(actionTypesDocuments.downloadFile, {
            name: PRICE_XLSX_FILE_NAME,
            currentPath: PRICE_XLSX_FOLDER_PATH,
          });
          const { url, fileName } = reportData;
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', fileName);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        } catch (error) {
          console.error('Ошибка загрузки прайса Excel:', error);
        } finally {
          this.downloadingPriceXlsx = false;
        }
      },
      toggleAccordion(groupName) {
        this.$set(this.openedDepartments, groupName, !this.openedDepartments[groupName]);
      },
      isAccordionOpen(groupName) {
        return !!this.openedDepartments[groupName];
      },
      hasPaidPackage(service) {
        return !!getPaidPackage(service);
      },
      packageKey(serviceId) {
        return String(serviceId == null ? '' : serviceId).trim();
      },
      isPackageOpen(serviceId) {
        const key = this.packageKey(serviceId);
        return !!(key && this.openedPackages[key]);
      },
      togglePackage(serviceId) {
        const key = this.packageKey(serviceId);
        if (!key) return;
        this.$set(this.openedPackages, key, !this.openedPackages[key]);
      },
      toggleAllAccordions() {
        const expand = !this.allExpanded;
        this.paidList.forEach((g) => {
          this.$set(this.openedDepartments, g.groupName, expand);
        });
      },
      openPopularGroup(groupName) {
        this.clearSearch();
        this.$set(this.openedDepartments, groupName, true);
        const anchor = this.groupAnchorId(groupName);
        // Сброс класса, чтобы анимация подсветки перезапускалась при повторном клике
        this.highlightedGroupAnchor = '';
        if (this.highlightClearTimer) {
          clearTimeout(this.highlightClearTimer);
          this.highlightClearTimer = null;
        }
        this.$nextTick(() => {
          this.highlightedGroupAnchor = anchor;
          this.highlightClearTimer = setTimeout(() => {
            this.highlightedGroupAnchor = '';
            this.highlightedServiceId = null;
            this.highlightClearTimer = null;
          }, 2500);
          // Скроллим к заголовку (кнопке), а не ко всему раскрытому блоку —
          // иначе при длинном списке услуг заголовок уезжает вверх, а не в центр.
          const el = document.getElementById(anchor);
          const header = el && el.querySelector('.paid-accordion__btn');
          const target = header || el;
          if (target && typeof target.scrollIntoView === 'function') {
            target.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });
      },
      toggleAccordionInfo() {
        this.isOpenInfo = !this.isOpenInfo;
      },
      routePaidServices() {
        this.$router.push('/documents/paid-services');
      },
      ensureSearchData() {
        if (!this.news || !this.news.length) {
          this.$store.dispatch(actionTypesNews.getNews).catch(() => {});
        }
        if (
          (!this.employees || !this.employees.length)
          && (!this.managements || !this.managements.length)
          && (!this.chiefMedicalOfficer || !this.chiefMedicalOfficer.length)
        ) {
          this.$store.dispatch(actionTypesDepartment.getAllDoctors).catch(() => {});
        }
        if (!this.publishedPages || !this.publishedPages.length) {
          this.$store.dispatch(`pages/${actionTypesPages.getPages}`).catch(() => {});
        }
      },
      onSearchInput() {
        this.runSearch(this.searchQuery);
      },
      runSearch(query) {
        this.searchResults = performSiteSearch(query, {
          paidServices: this.paid,
          publishedPages: this.publishedPages,
          news: this.news,
          employees: this.employees,
          managements: this.managements,
          chiefMedicalOfficer: this.chiefMedicalOfficer,
        });
      },
      clearSearch() {
        this.searchQuery = '';
        this.searchResults = [];
      },
      paidServiceRowId(serviceId) {
        const id = serviceId != null ? String(serviceId) : '';
        return id ? `paid-svc-${encodeURIComponent(id)}` : '';
      },
      isHighlightedService(serviceId) {
        const sid = serviceId != null ? String(serviceId).trim() : '';
        return sid && this.highlightedServiceId === sid;
      },
      normalizeQueryParam(val) {
        if (val == null) {
          return '';
        }
        const s = Array.isArray(val) ? val[0] : val;
        return typeof s === 'string' ? s.trim() : String(s).trim();
      },
      findGroupForService(serviceId, groupHint) {
        const needle = String(serviceId || '').trim();
        if (!needle || !(this.paid || []).length) {
          return null;
        }
        const hint = this.normalizeQueryParam(groupHint);
        if (hint) {
          const byHint = this.paid.find(
              (g) =>
                  g &&
                  String(g.groupName || '') === hint &&
                  (g.services || []).some((s) => String(s.id || '').trim() === needle)
          );
          if (byHint) {
            return byHint;
          }
        }
        return (
            this.paid.find((g) =>
                (g.services || []).some((s) => String(s.id || '').trim() === needle)
            ) || null
        );
      },
      scrollPaidServiceRowIntoView(rowEl) {
        if (!rowEl || typeof rowEl.scrollIntoView !== 'function') {
          return;
        }
        rowEl.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest',
        });
      },
      applyServiceFromRoute() {
        const serviceId = this.normalizeQueryParam(this.$route.query.serviceId);
        if (!serviceId) {
          return;
        }
        const group = this.findGroupForService(serviceId, this.$route.query.group);
        if (!group) {
          return;
        }
        this.$set(this.openedDepartments, group.groupName, true);
        if (getPaidPackage({ id: serviceId })) {
          this.$set(this.openedPackages, serviceId, true);
        }
        this.highlightedServiceId = serviceId;
        this.highlightedGroupAnchor = this.groupAnchorId(group.groupName);
        if (this.highlightClearTimer) {
          clearTimeout(this.highlightClearTimer);
          this.highlightClearTimer = null;
        }
        this.highlightClearTimer = setTimeout(() => {
          this.highlightedServiceId = null;
          this.highlightedGroupAnchor = '';
          this.highlightClearTimer = null;
        }, 2500);
        this.$nextTick(() => {
          this.$nextTick(() => {
            const elId = this.paidServiceRowId(serviceId);
            const el = elId ? document.getElementById(elId) : null;
            if (el) {
              this.scrollPaidServiceRowIntoView(el);
            }
          });
        });
      },
    },
    mounted() {
      this.ensureSearchData();
      this.$store.dispatch(actionTypesPaid.getPaid)
          .then(() => {
            this.$nextTick(() => this.applyServiceFromRoute());
          })
          .catch((er) => {
            console.log('Ошибка', er);
          });
    },
    beforeDestroy() {
      if (this.highlightClearTimer) {
        clearTimeout(this.highlightClearTimer);
        this.highlightClearTimer = null;
      }
    },
  };
</script>
